import { createSlice, current } from '@reduxjs/toolkit';
import Decimal from 'decimal.js-light';

import { waterLevelStatus } from '@/constants/index';
import type {
  GraphValueData,
  GraphData,
  MessageForGraphRainfall,
  MessageForGraphWaterLevel,
  Spot,
  Threshold,
  ThresholdLine,
  AssetTypeItem,
} from '@/types/index';
import { dateFormat } from '@/utils/date';

export interface SpotDetailState {
  targetSpotId: string;
  targetSpotTypeId: string | undefined;
  targetSpotType: AssetTypeItem | undefined;
  isSpotPopUpOpen: boolean;
  isLayerPopUpOpened: boolean;
  graphData: GraphData;
  longTermGraphData: GraphData;
  spotDetail: Spot;
  isShowBackButton: boolean;
  isShowLayerDetailBackButton: boolean;
  isErrorConfiguration: boolean;
  targetFavoritePointId: string;
  targetFavoritePointName: string;
  targetFavoritePointLat: number | null;
  targetFavoritePointLng: number | null;
  targetFavoritePointZoom: number | null;
  targetFavoritePointMapType: string;
  isFavoritePointPopUpOpen: boolean;
}

const initialState: SpotDetailState = {
  targetSpotId: '',
  targetSpotTypeId: undefined,
  targetSpotType: undefined,
  isSpotPopUpOpen: false,
  isLayerPopUpOpened: false,
  graphData: {},
  longTermGraphData: {},
  spotDetail: {} as Spot,
  isShowBackButton: false,
  isShowLayerDetailBackButton: false,
  isErrorConfiguration: false,
  targetFavoritePointId: '',
  targetFavoritePointName: '',
  targetFavoritePointLat: null,
  targetFavoritePointLng: null,
  targetFavoritePointZoom: null,
  targetFavoritePointMapType: '',
  isFavoritePointPopUpOpen: false,
};

export const spotDetailSlice = createSlice({
  name: 'spotDetail',
  initialState,
  reducers: {
    setTargetSpot: (state, action) => {
      state.targetSpotId = action.payload.spotId;
      state.targetSpotTypeId = action.payload.spotTypeId;
      state.isSpotPopUpOpen = action.payload.isOpen;
      if (action.payload.isOpen === true) {
        state.isFavoritePointPopUpOpen = false;
      }
    },
    setTargetSpotType: (state, action) => {
      state.targetSpotType = action.payload;
    },
    setSpotDetail: (state, action) => {
      state.spotDetail = action.payload;
    },
    setLayerPopUpIsOpened: (state, action) => {
      state.isLayerPopUpOpened = action.payload;
    },
    setIsShowBackButton: (state, action) => {
      state.isShowBackButton = action.payload;
    },
    setIsShowLayerDetailBackButton: (state, action) => {
      state.isShowLayerDetailBackButton = action.payload;
    },
    setIsErrorConfiguration: (state, action) => {
      state.isErrorConfiguration = action.payload;
    },
    setTargetFavoritePoint: (state, action) => {
      state.targetFavoritePointId = action.payload.id ?? '';
      state.targetFavoritePointName = action.payload.name ?? '';
      state.targetFavoritePointLat = action.payload.lat ?? null;
      state.targetFavoritePointLng = action.payload.lng ?? null;
      state.targetFavoritePointZoom = action.payload.zoom ?? null;
      state.targetFavoritePointMapType = action.payload.mapType ?? '';
      state.isFavoritePointPopUpOpen = action.payload.isOpen;
      if (action.payload.isOpen === true) {
        state.isSpotPopUpOpen = false;
        state.targetSpotId = '';
        state.targetSpotTypeId = undefined;
        state.targetSpotType = undefined;
      }
    },
    setMessages: (state, action) => {
      const isLongTermMessages = Boolean(action.payload.isLongTermMessages);
      const messages = action.payload.results.messages;
      const tmpCustomComponents = action.payload.customComponents;
      const customComponents = tmpCustomComponents ?? '';
      const targetKey = isLongTermMessages ? 'longTermGraphData' : 'graphData';
      if (customComponents.includes('rainfallGraph') === true) {
        const graphData = messages.map((rainfall: MessageForGraphRainfall) => {
          return {
            label: dateFormat(
              rainfall.properties.time ?? rainfall.createdAt,
              isLongTermMessages ? 'M月D日\nHH:mm' : 'HH:mm'
            ),
            value: rainfall.properties.precipitationAmount,
          };
        });
        const label = graphData.map((item: GraphValueData) => item.label);
        const value = graphData.map((item: GraphValueData) => item.value);
        const maxValue = messages
          .map((rainfall: MessageForGraphRainfall) =>
            rainfall.properties.precipitationAmount
              ? rainfall.properties.precipitationAmount
              : 0
          )
          .reduce((a: number, b: number) => Math.max(a, b));
        const yAxisMax = maxValue > 15 ? maxValue : 15;

        const splitNumber = Math.round(new Decimal(yAxisMax).div(5).toNumber());

        state[targetKey] = {
          data: { label, value },
          options: { splitNumber, yAxisMax },
        };
      } else if (customComponents.includes('waterLevelGraph') === true) {
        const graphData = messages.map(
          (waterLevel: MessageForGraphWaterLevel) => {
            return {
              label: dateFormat(
                waterLevel.properties.time ?? waterLevel.createdAt,
                isLongTermMessages ? 'M月D日\nHH:mm' : 'HH:mm'
              ),
              value: waterLevel.properties.waterLevel,
            };
          }
        );
        const label = graphData.map((item: GraphValueData) => item.label);
        const value = graphData.map((item: GraphValueData) => item.value);
        const waterLevelDetail = current(state.spotDetail);
        const waterLevelDetailAttributes = waterLevelDetail.attributes;
        const calculateRound = (number: number) => {
          return Math.round(number * 10) / 10;
        };
        if (!waterLevelDetailAttributes) return;

        const thresholds = Object.keys(waterLevelDetailAttributes)
          .map((attributes: string) => {
            switch (attributes) {
              case 'lowerLimitOfAGraph':
                return {
                  color: '#29cc5f',
                  label: [
                    waterLevelDetailAttributes.lowerLimitOfAGraph,
                    calculateRound(
                      waterLevelDetailAttributes.thresholdReport - 0.1
                    ) + 'm',
                  ],
                  value: [
                    waterLevelDetailAttributes.lowerLimitOfAGraph,
                    calculateRound(
                      waterLevelDetailAttributes.thresholdReport - 0.1
                    ),
                  ],
                };
              case 'thresholdReport':
                return {
                  color: '#fad532',
                  label: [
                    waterLevelDetailAttributes.thresholdReport,
                    calculateRound(
                      waterLevelDetailAttributes.thresholdVigilance - 0.1
                    ) + 'm',
                  ],
                  value: [
                    calculateRound(
                      waterLevelDetailAttributes.thresholdReport - 0.1
                    ),
                    calculateRound(
                      waterLevelDetailAttributes.thresholdVigilance - 0.1
                    ),
                  ],
                  name: waterLevelStatus.NOTIFICATION,
                  colorForThresholdLine: '#FAD532',
                  valueForThresholdLine:
                    waterLevelDetailAttributes.thresholdReport,
                };
              case 'thresholdVigilance':
                if (
                  waterLevelDetailAttributes.thresholdSpecialVigilance == null
                ) {
                  return {
                    color: '#fa7532',
                    label: [
                      ` ${waterLevelDetailAttributes.thresholdVigilance}m ~ `,
                    ],
                    value: [
                      calculateRound(
                        waterLevelDetailAttributes.thresholdVigilance - 0.1
                      ),
                    ],
                    name: waterLevelStatus.ALERT,
                    colorForThresholdLine: '#FA7532',
                    valueForThresholdLine:
                      waterLevelDetailAttributes.thresholdVigilance,
                  };
                } else {
                  return {
                    color: '#fa7532',
                    label: [
                      waterLevelDetailAttributes.thresholdVigilance,
                      calculateRound(
                        waterLevelDetailAttributes.thresholdSpecialVigilance -
                          0.1
                      ) + 'm',
                    ],
                    value: [
                      calculateRound(
                        waterLevelDetailAttributes.thresholdVigilance - 0.1
                      ),
                      calculateRound(
                        waterLevelDetailAttributes.thresholdSpecialVigilance -
                          0.1
                      ),
                    ],
                    name: waterLevelStatus.ALERT,
                    colorForThresholdLine: '#FA7532',
                    valueForThresholdLine:
                      waterLevelDetailAttributes.thresholdVigilance,
                  };
                }

              case 'thresholdSpecialVigilance':
                return {
                  color: '#f03043',
                  label: [
                    waterLevelDetailAttributes.thresholdSpecialVigilance,
                    calculateRound(
                      waterLevelDetailAttributes.thresholdSpecialDanger - 0.1
                    ) + 'm',
                  ],
                  value: [
                    calculateRound(
                      waterLevelDetailAttributes.thresholdSpecialVigilance - 0.1
                    ),
                    calculateRound(
                      waterLevelDetailAttributes.thresholdSpecialDanger - 0.1
                    ),
                  ],
                  name: waterLevelStatus.SPECIAL_ALERT,
                  colorForThresholdLine: '#F03043',
                  valueForThresholdLine:
                    waterLevelDetailAttributes.thresholdSpecialVigilance,
                };
              case 'thresholdSpecialDanger':
                return {
                  color: '#8f52cc',
                  label: [
                    `${waterLevelDetailAttributes.thresholdSpecialDanger}m ~ `,
                  ],
                  value: [
                    calculateRound(
                      waterLevelDetailAttributes.thresholdSpecialDanger - 0.1
                    ),
                  ],
                  name: waterLevelStatus.DANGEROUS,
                  colorForThresholdLine: '#8F52CC',
                  valueForThresholdLine:
                    waterLevelDetailAttributes.thresholdSpecialDanger,
                };
              default:
                return {};
            }
          })
          .filter(Boolean);

        const threshold = thresholds
          .map((item) => {
            if (
              item.color != null &&
              item.label.length > 0 &&
              item.value.length > 0
            ) {
              return {
                color: item.color,
                label: item.label,
                value: item.value,
              };
            } else {
              return undefined;
            }
          })
          .filter(Boolean)
          //valueの昇順ソート
          .sort((a, b) => {
            if (typeof a === 'undefined' || typeof b === 'undefined') return 0;
            if (
              typeof a.value[0] === 'undefined' ||
              typeof b.value[0] === 'undefined'
            )
              return 0;

            return a.value[0] < b.value[0] ? -1 : 1;
          });

        const thresholdLine = thresholds
          .map((item) => {
            if (typeof item === 'undefined') return;
            if (
              item.colorForThresholdLine != null &&
              item.valueForThresholdLine != null
            ) {
              return {
                color: item.colorForThresholdLine,
                value: item.valueForThresholdLine,
                name: item.name,
              };
            }
          })
          .filter(Boolean);

        const checkThreshold = (
          item: (Threshold | ThresholdLine | undefined)[]
        ) => {
          if (thresholdLine.length > 0) {
            return item;
          } else {
            return;
          }
        };
        const checkImageUrl = () => {
          return waterLevelDetailAttributes.waterLevelGraph?.imageUrl ?? null;
        };
        state[targetKey] = {
          data: { label, value },
          options: {
            waterLevelGraphImage: checkImageUrl(),
            splitNumber: waterLevelDetailAttributes.upperLimitOfAGraph + 2,
            yAxisMax: waterLevelDetailAttributes.upperLimitOfAGraph,
            yAxisMin: waterLevelDetailAttributes.lowerLimitOfAGraph,
          },
          threshold: {
            threshold: checkThreshold(threshold) as Threshold[],
            thresholdLine: checkThreshold(thresholdLine) as ThresholdLine[],
          },
        };
      }
    },
  },
});

export const {
  reducer: spotDetailReducer,
  actions: {
    setTargetSpot,
    setTargetSpotType,
    setSpotDetail,
    setLayerPopUpIsOpened,
    setMessages,
    setIsShowBackButton,
    setIsShowLayerDetailBackButton,
    setIsErrorConfiguration,
    setTargetFavoritePoint,
  },
} = spotDetailSlice;
